/**
 * Modèle PlayerStep - Identique à Flutter
 */

import {PlayerAction, PlayerActionFromJson, ActionObjectFromJson} from './PlayerAction';
import {PlayerPath, PlayerPathFromJson} from './PlayerPath';
import {GameState} from './GameState';
import {PlayerNotification, PlayerNotificationFromJson} from './PlayerNotification';
import {PlayerStepAttribute, PlayerStepAttributeFromJson} from './PlayerStepAttribute';
import {SolidObject, SolidObjectFromJson} from './SolidObject';
import {PlayerCondition, PlayerConditionFromJson} from './PlayerCondition';
import {PlayerEvent, PlayerEventFromJson} from './PlayerEvent';

export interface PlayerStepExtraField {
  type: string;
  value?: string | number;
  currentValue?: string | number;
  initValue?: string | number;
  [key: string]: any;
}

export interface PlayerStep {
  numero: number;
  name: string;
  description: string;
  image?: string;
  type: string;
  paths: PlayerPath[];
  actions: PlayerAction[];
  events?: any[]; // Événements bruts (pas encore parsés en PlayerEvent)
  notebook?: string;
  notifications: PlayerNotification[];
  attributes: PlayerStepAttribute[];
  gameState?: GameState;
  objects?: SolidObject[];
  id: string;
  title: string;
  conditions: PlayerCondition[];
  playerEvents: PlayerEvent[]; // Événements parsés
  attribute?: string;
  value?: string;
  extraFields: PlayerStepExtraField[];
  layerAdventurer?: {[key: string]: any}; // Layer de l'avatar du joueur
  allPaths?: PlayerPath[]; // Tous les paths originaux (avant filtrage) pour le mode éditeur
}

/**
 * Parse un PlayerStep depuis JSON
 * Identique à Flutter PlayerStep.fromJson
 */
export const PlayerStepFromJson = (json: any): PlayerStep => {
  if (!json || typeof json.numero !== 'number') {
    throw new Error('Invalid PlayerStep JSON: missing numero');
  }

  const type = json.type || json.typeName || 'STEP_SIMPLE';

  // Debug: log notebook value from API (seulement pour les étapes avec notebook non-null)
  if (json.numero && json.notebook != null && json.notebook !== '') {
    console.log(`[PlayerStepFromJson] Step ${json.numero}: notebook from API =`, json.notebook);
    console.log(`[PlayerStepFromJson] Step ${json.numero}: notebook type =`, typeof json.notebook);
    console.log(`[PlayerStepFromJson] Step ${json.numero}: notebook length =`, typeof json.notebook === 'string' ? json.notebook.length : 'N/A');
  }

  return {
    numero: Number(json.numero),
    name: json.name || '',
    description: json.description || '',
    image: json.image,
    type,
    paths: (json.paths as any[] || []).map((p) => PlayerPathFromJson(p)),
    actions: (json.actions as any[] || []).map((a) => PlayerActionFromJson(a)),
    events: json.events as any[] | undefined,
    // Normaliser notebook : null/undefined -> undefined, string vide -> undefined, string non-vide -> string
    notebook: json.notebook && typeof json.notebook === 'string' && json.notebook.trim() ? json.notebook.trim() : undefined,
    notifications: (json.notifications as any[] || []).map((n) => PlayerNotificationFromJson(n)),
    attributes: (json.attributes as any[] || []).map((a) => PlayerStepAttributeFromJson(a)),
    gameState: json.gameState ? (json.gameState as GameState) : undefined,
    objects: (json.objects as any[] || [])?.map((o) => SolidObjectFromJson(o)).filter((o): o is NonNullable<typeof o> => o !== null),
    id: json.id || '',
    title: json.title || json.name || '',
    conditions: (json.conditions as any[] || []).map((c) => PlayerConditionFromJson(c)),
    playerEvents: (json.playerEvents as any[] || []).map((e) => PlayerEventFromJson(e)),
    attribute: json.attribute,
    value: json.value,
    extraFields: (json.extraFields as any[] || []).map((f) => ({
      type: f.type || '',
      value: f.value,
      currentValue: f.currentValue,
      initValue: f.initValue,
      ...f,
    })),
    layerAdventurer: json.layerAdventurer ? (json.layerAdventurer as {[key: string]: any}) : undefined,
  };
};
